import {
    HttpClient,
    HttpParams
} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {environment} from "src/environments/environment";
import {Attribute} from "../../classes/rest-classes/Attribute";
import {
    AttributeDetail,
    AttributeValue
} from "../../classes/rest-classes/AttributeDetail";
import {DemandsAndNeeds} from "../../classes/rest-classes/DemandsAndNeeds";
import {Price} from "../../classes/rest-classes/Price";
import {Product as RestProduct} from "../../classes/rest-classes/Product";
import {
    StatementOfFact,
    StatementOfFactResponse
} from "../../classes/rest-classes/StatementOfFact";
import {BaseRestService} from "./baseRestService"
import {ProductProvider} from "../../classes/ProductProvider";
import CacheService from "../cacheService";
import {Product} from "../../classes/Product";

@Injectable({
    providedIn: 'root'
})

export class RestProductsService extends BaseRestService {
    private readonly BASE_URL: string;

    constructor(private http: HttpClient,
                private cacheService: CacheService
    ) {
        super();
        this.BASE_URL = environment.restApiUrl + '/Products';
    }


    public productsByType(id: string): Observable<RestProduct[]> {
        const url = `${this.BASE_URL}/GetByType`;
        let queryParams = new HttpParams();
        queryParams = queryParams.append('productType', id);
        return this.http.get<RestProduct[]>(url, {params: queryParams, headers: this.authHeaders});
    }

    public getAttributesByProductId(id: Number): Observable<Attribute[]> {
        const url = `${this.BASE_URL}/${id}/Attributes`;
        return this.http.get<Attribute[]>(url, {headers: this.authHeaders});
    }

    public getAttributeDetails(id: Number, name: string): Observable<AttributeDetail[]> {
        const url = `${this.BASE_URL}/${id}/Attributes/${name}`;
        return this.http.get<AttributeDetail[]>(url, {headers: this.authHeaders});
    }

    public async getSOFByProductId(productId: number): Promise<StatementOfFactResponse[]> {
        const url = `${this.BASE_URL}/StatementsOfFact`;
        let queryParams = new HttpParams();
        queryParams = queryParams.append('productIds', productId);

        let item = this.cacheService.getCacheItem<StatementOfFactResponse[]>(`/Products/StatementsOfFact?productIds_${productId}`);
        if (item) {
            return item;
        }

        item = await this.http.get<StatementOfFactResponse[]>(url, {params: queryParams, headers: this.authHeaders}).toPromise();
        return this.cacheService.addOrReplaceCacheItem(`/Products/StatementsOfFact?productIds_${productId}`, item);
    }

    public getPrice(productId: number, productAttributes: Attribute[]): Observable<Price> {
        // Setting the default values where the value is null for EXECUTORTYPE and UKRESIDENT attributes, as they are expected to have a value at all times.
        const updatedAttributes = productAttributes.map(attribute => ({
            ...attribute,
            value: (attribute.value !== null && attribute.value !== "") ? attribute.value :
                (attribute.name.includes("EXECUTORTYPE") ? "PROCURATOR" :
                    (attribute.name.includes("UKRESIDENT") ? "TRUE" : null))
        }));
        const url = `${this.BASE_URL}/${productId}/Price`;
        const filteredAttributes = updatedAttributes.filter(atr => !atr.isExcluded);
        return this.http.post<Price>(url, filteredAttributes, {headers: this.authHeaders});
    }

    public getDemandsAndNeeds(ids: number[]): Observable<DemandsAndNeeds[]> {
        const url = `${this.BASE_URL}/DemandsAndNeeds`;
        let queryParams = new HttpParams();
        ids.forEach(id => {
            queryParams = queryParams.append('productIds', id);
        })
        return this.http.get<DemandsAndNeeds[]>(url, {params: queryParams, headers: this.authHeaders});
    }

    public getProduct(productId: number): Observable<RestProduct> {
        const url = `${this.BASE_URL}/${productId}`;
        return this.http.get<RestProduct>(url, {headers: this.authHeaders});
    }

    public getProducts(productIds: number[]): Observable<RestProduct[]> {
        const url = `${this.BASE_URL}/Get`;
        let queryParams = new HttpParams();
        productIds.forEach(id => {
            queryParams = queryParams.append('productIds', id);
        })
        return this.http.get<RestProduct[]>(url, {params: queryParams, headers: this.authHeaders});
    }

    public getProductAttributes(productIds: number[]): Observable<Product[]> {
        const url = `${this.BASE_URL}/GetProductDetails`;
        let queryParams = new HttpParams();
        productIds.forEach(id => {
            queryParams = queryParams.append('productIds', id);
        })
        return this.http.get<Product[]>(url, {params: queryParams, headers: this.authHeadersV2});
    }

    public getBasicProduct(productIds: number[]): Observable<Product[]> {
        const url = `${this.BASE_URL}/GetBasicProduct`;
        let queryParams = new HttpParams();
        productIds.forEach(id => {
            queryParams = queryParams.append('productIds', id);
        })
        return this.http.get<Product[]>(url, {params: queryParams, headers: this.authHeadersV2});
    }

    public async getAllProductsAllowedForUser(): Promise<any[]> {
        let queryParams = new HttpParams();

        let item = this.cacheService.getCacheItem<any[]>('Products');
        if (item) {
            return item;
        }

        item = await this.http.get<any[]>(this.BASE_URL, {params: queryParams, headers: this.authHeaders}).toPromise();
        
        const searchInsuranceWarningText = " - RUN-OFF for drafts only";
        /*
        Removed #32485
        item.forEach(i=>{
            //update website-only rendering of the name for the invalid search products
            if([51, 260, 261, 262, 263, 264, 265].includes(i.productId)){
                if(!i.name.endsWith(searchInsuranceWarningText))
                    i.name = i.name + searchInsuranceWarningText;
            }
        });*/
        return this.cacheService.addOrReplaceCacheItem('Products', item);
    }


    public GetProviderPdf(providerId: number): Observable<string> {
        const url = `${this.BASE_URL}/${providerId}/Provider`;
        return this.http.get<string>(url, {headers: this.authHeaders});
    }
    public async getStatementOfFacts(productId: number): Promise<StatementOfFact> {
        const url = `${this.BASE_URL}/${productId}/StatementsOfFact`;

        let item = this.cacheService.getCacheItem<StatementOfFact>(`/Products/${productId}/StatementsOfFact`);
        if (item) {
            return item;
        }

        item = await this.http.get<StatementOfFact>(url, {headers: this.authHeaders}).toPromise();
        return this.cacheService.addOrReplaceCacheItem(`/Products/${productId}/StatementsOfFact`, item);
    }
    
    public getAttributeDetail(id: Number, name: string): Observable<AttributeDetail | null> {
        // OMG how it possible that getAttributeDetails(id: Number, name: string) returns array?!
        return this.getAttributeDetails(id, name)
            .pipe(
                map((details: AttributeDetail[]) => details.find(x => x.name == name) || null)
            );
    }

    public getAttributeValues(id: Number, name: string): Observable<AttributeValue[]> {
        return this.getAttributeDetail(id, name)
            .pipe(
                map((detail: AttributeDetail) => detail?.attributeValues || [])
            );
    }
}